import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Modal.module.css';

// Lets the shared Modal wire its dialog's accessible name to the <h2> that
// ModalHeader renders, without the calling component having to thread an id
// through both. Modal generates the id and reads it back for aria-labelledby;
// ModalHeader stamps it onto its heading AND registers that a title exists, so
// the dialog only emits aria-labelledby when a header title is actually
// present (no dangling IDREF for a headerless modal). A ModalHeader rendered
// outside a Modal simply gets no id and no registration (context is null),
// which is harmless.
interface ModalContextValue {
  titleId: string;
  registerTitle: (present: boolean) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusable = (root: HTMLElement | null): HTMLElement[] =>
  root
    ? Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    : [];

interface ModalProps {
  className?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  ariaLabel?: string;
}

/**
 * Shared modal shell. Renders a proper dialog: role="dialog" + aria-modal,
 * an accessible name (from the ModalHeader title, or an explicit `ariaLabel`),
 * Escape-to-close, a Tab focus trap, and focus restoration to whatever was
 * focused before the dialog opened.
 *
 * @param {string} [className] extra scoped class for the dialog container
 * @param {Function} [onClose] invoked on Escape; omit to disable Escape-to-close
 * @param {string} [ariaLabel] accessible name when there is no ModalHeader title
 */
export const Modal = ({
  className = '',
  children,
  onClose,
  ariaLabel,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Set true only once a ModalHeader with a non-empty title registers, so the
  // dialog's aria-labelledby points at a real, rendered heading id.
  const [hasTitle, setHasTitle] = useState(false);
  const registerTitle = useCallback((present: boolean) => {
    setHasTitle(present);
  }, []);
  const contextValue = useMemo<ModalContextValue>(
    () => ({ titleId, registerTitle }),
    [titleId, registerTitle]
  );

  // Escape-to-close. Only wired when the caller provides onClose.
  useEffect(() => {
    if (!onClose) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap + focus restore. Runs once for the dialog's lifetime.
  useEffect(() => {
    const dialog = dialogRef.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Move focus into the dialog: first focusable element, else the dialog.
    const focusable = getFocusable(dialog);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (dialog) {
      dialog.focus();
    }

    const handleTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialog) {
        return;
      }
      const items = getFocusable(dialog);
      if (items.length === 0) {
        // Nothing tabbable — keep focus on the dialog itself.
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    if (dialog) {
      dialog.addEventListener('keydown', handleTrap);
    }
    return () => {
      if (dialog) {
        dialog.removeEventListener('keydown', handleTrap);
      }
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <div className={styles['modal-overlay']}>
      <div
        ref={dialogRef}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || undefined}
        aria-labelledby={!ariaLabel && hasTitle ? titleId : undefined}
        tabIndex={-1}
      >
        <ModalContext.Provider value={contextValue}>
          {children}
        </ModalContext.Provider>
      </div>
    </div>
  );
};

interface ModalHeaderProps {
  title?: React.ReactNode;
  onClose?: () => void;
  disabled?: boolean;
}

export const ModalHeader = ({
  title,
  onClose,
  disabled = false,
}: ModalHeaderProps) => {
  const context = useContext(ModalContext);
  const registerTitle = context ? context.registerTitle : null;
  const hasTitle = title != null && title !== '';

  // Tell the enclosing Modal whether a real title is present, so it emits
  // aria-labelledby only when this heading actually names the dialog.
  useEffect(() => {
    if (!registerTitle) {
      return undefined;
    }
    registerTitle(hasTitle);
    return () => registerTitle(false);
  }, [registerTitle, hasTitle]);

  return (
    <div className={styles['modal-header']}>
      <h2 id={context && hasTitle ? context.titleId : undefined}>{title}</h2>
      <button
        type="button"
        onClick={onClose}
        className={styles['close-btn']}
        disabled={disabled}
        aria-label="Close dialog"
      >
        ×
      </button>
    </div>
  );
};

interface ModalBodyProps {
  className?: string;
  children?: React.ReactNode;
}

// className is an optional extra scoped class from the calling component's
// own module.css, appended after modal-body -- lets a modal (e.g.
// CalendarEntryEditModal) add its own layout rules to this shared body
// element without a cross-file descendant selector, which CSS Modules'
// per-file class hashing would silently break.
export const ModalBody = ({ className = '', children }: ModalBodyProps) => (
  <div className={`${styles['modal-body']} ${className}`.trim()}>
    {children}
  </div>
);

interface ModalFooterProps {
  left?: React.ReactNode;
  children?: React.ReactNode;
}

export const ModalFooter = ({ left = null, children }: ModalFooterProps) => (
  <div className={styles['modal-footer']}>
    <div className={styles['footer-left']}>{left}</div>
    <div className={styles['footer-right']}>{children}</div>
  </div>
);
